import { Fragment, h } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import useResponsive from 'utils/responsive';
import VariantSelector from './variant-selector';
import Price from './price';
import Accordions from './product-accordions';
import QuantitySelector from './quantity-selector';
import AddToCartButton from './add-to-cart-button';
import ProductGallery from './product-gallery';
import Features from './product-features';
import GroupedProducts from './grouped-products';
import useUniqueId from '../utils/unique-id';
import richTextToHTML from '../utils/rich-text-to-html';

const ProductForm = ({ openCustomizerStyleModal, ...props }) => {
  const uniqueId = useUniqueId();

  const getPromoBadge = (variant) => {
    // Prioritize variant-level badge
    if (variant.promoBadge && Object.keys(variant.promoBadge).length > 0) {
      return variant.promoBadge;
    }
    // Fall back to product-level badge
    if (variant.productPromoBadge && Object.keys(variant.productPromoBadge).length > 0) {
      return variant.productPromoBadge;
    }
    return null;
  };

  const [currentVariant, setCurrentVariant] = useState(
    props.product.variants.find(
      (v) =>
        JSON.stringify(v.selectedOptions) === JSON.stringify(props.product.variantBySelectedOptions.selectedOptions),
    ),
  );

  useEffect(() => {
    const handleVariantUpdate = (event) => {
      if (!event.detail?.currentVariant) return;
      setCurrentVariant(event.detail.currentVariant);
    };

    document.addEventListener('variantUpdate', handleVariantUpdate);
    return () => {
      document.removeEventListener('variantUpdate', handleVariantUpdate);
    };
  }, [setCurrentVariant]);

  const [quantity, setQuantity] = useState(1);
  const [showStickyAddToCart, setShowStickyAddToCart] = useState(false);
  const [checkedOptions, setCheckedOptions] = useState(
    props.product.variants.length === 1
      ? { weight: true, size: true }
      : {
          weight:
            props.product.options.findIndex((opt) => opt.name.toLowerCase() === 'fabric weight') === -1 ||
            props.product.options.find(
              (opt) => opt.name.toLowerCase() === 'fabric weight' && opt.optionValues.length === 1,
            ),
          size: props.product.options.findIndex((opt) => opt.name.toLowerCase() === 'size') === -1,
        },
  );
  const maxAvailableInventory = currentVariant?.inventory?.quantity || 0;

  const addToCartRef = useRef(null);
  const { isMobile } = useResponsive();

  useEffect(() => {
    if (!currentVariant) return;

    // Only restrict quantity if inventory is tracked AND deny policy
    if (currentVariant.inventoryManagement === 'shopify' && currentVariant.inventoryPolicy === 'deny') {
      // Ensure quantity is at least 1, even when inventory is 0
      setQuantity((prev) => Math.max(1, Math.min(prev, maxAvailableInventory || 1)));
    }
  }, [currentVariant, maxAvailableInventory]);

  const handleQuantityChange = (newQuantity) => {
    if (newQuantity < 1) return;

    // Only validate quantity if inventory is tracked AND deny policy
    if (
      currentVariant.inventoryManagement === 'shopify' &&
      currentVariant.inventoryPolicy === 'deny' &&
      newQuantity > maxAvailableInventory
    ) {
      // Ensure quantity is at least 1, even when inventory is 0
      setQuantity(Math.max(1, maxAvailableInventory));
      return;
    }

    setQuantity(newQuantity);
  };

  // Check if the product has the 'gift_box_eligible' tag AND is not a dropship item
  const isGiftBoxEligible =
    props.product.tags.some(
      (tag) => tag.toLowerCase() === 'gift_box_eligible' || tag.toLowerCase() === 'gift_box_eligible::true',
    ) &&
    !props.product.tags.some(
      (tag) =>
        tag.toLowerCase() === 'dropship' ||
        tag.toLowerCase() === 'dropship::true' ||
        tag.toLowerCase() === 'custom.dropship',
    );

  const compareAtPrice = parseFloat(currentVariant.compareAtPrice?.amount || 0);
  const salePrice = parseFloat(currentVariant.price?.amount || 0);

  const handleAddToCart = (e) => {
    e.preventDefault();

    const payload = {
      merchandiseId: currentVariant.id,
      quantity,
      attributes: [
        {
          key: '_inventoryManagement',
          value: currentVariant.inventoryManagement,
        },
        {
          key: '_inventoryPolicy',
          value: currentVariant.inventoryPolicy,
        },
        ...(currentVariant.requiresShipping
          ? [{ key: '_gift_box_eligible', value: isGiftBoxEligible ? 'true' : 'false' }]
          : []),
      ],
    };

    const promoBadge = getPromoBadge(currentVariant);
    if (promoBadge) {
      payload.attributes.push(
        {
          key: '_promoBadgeText',
          value: promoBadge.copy,
        },
        {
          key: '_promoBadgeTextColor',
          value: promoBadge.copy_color,
        },
        {
          key: '_promoBadgeBackgroundColor',
          value: promoBadge.background_color,
        },
      );
    }

    if (delayedShippingMessage) {
      payload.attributes.push({
        key: '_delayedShippingMessage',
        value: delayedShippingMessage,
      });
    }
    if (compareAtPrice > salePrice) {
      payload.attributes.push({
        key: '_compare_at_price',
        value: compareAtPrice,
      });
    }

    // Check if requiresShipping exists and is false
    if (currentVariant.requiresShipping === false) {
      payload.attributes.push({ key: '_electronic', value: 'true' });
    }

    // Dispatch the event with the updated payload
    const addToCartEvent = new CustomEvent('cart:add', {
      detail: { payload },
    });

    document.dispatchEvent(addToCartEvent);
  };

  // get delayed shipping message based on currently selected variant
  const delayedShippingMessageData = currentVariant?.delayedShippingMessage?.description;
  const delayedShippingMessage = useMemo(() => richTextToHTML(delayedShippingMessageData), [currentVariant]);

  const variantSelectors = useMemo(
    () =>
      !props.product.hasOnlyDefaultVariant && (
        <div className="my-20 flex flex-col gap-20">
          {props.product.options.map((option) => (
            <VariantSelector
              product={props.product}
              currentVariant={currentVariant}
              key={option.name}
              option={option}
              setCurrentVariant={setCurrentVariant}
              checkedOptions={checkedOptions}
              setCheckedOptions={setCheckedOptions}
              prefix={uniqueId}
              filteredVariants={props.filteredVariants}
            />
          ))}
        </div>
      ),

    [currentVariant, checkedOptions, props.filteredVariants],
  );

  const price = useMemo(
    () => (
      <Price
        currentVariant={currentVariant}
        discountPromo={getPromoBadge(currentVariant)}
        product={props.product}
        isProductForm={true}
      />
    ),
    [currentVariant],
  );

  const features = useMemo(() => <Features currentVariant={currentVariant} />, [currentVariant]);
  const gallery = useMemo(
    () => (
      <ProductGallery
        filteredVariants={props.filteredVariants}
        {...props}
      />
    ),
    [isMobile],
  );

  const addToCartButton = useMemo(
    () => (
      <AddToCartButton
        currentVariant={currentVariant}
        handleAddToCart={handleAddToCart}
        checkedOptions={checkedOptions}
        isGiftCard={props.product.isGiftCard}
        disabled={!currentVariant.availableForSale || quantity > maxAvailableInventory}
      />
    ),
    [currentVariant, quantity, checkedOptions, currentVariant.availableForSale, maxAvailableInventory],
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const id = currentVariant?.id.split('/ProductVariant/')[1];
    searchParams.set('variant', id);
    const newRelativePathQuery = `${window.location.pathname}?${searchParams.toString()}`;
    window.history.replaceState(null, '', newRelativePathQuery);
  }, [currentVariant]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowStickyAddToCart(entry.boundingClientRect.top < 0 && !entry.isIntersecting);
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: [0, 1],
      },
    );

    if (addToCartRef.current) {
      observer.observe(addToCartRef.current);
    }

    return () => {
      if (addToCartRef.current) {
        observer.unobserve(addToCartRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (props.product.isGiftCard) {
      const setupRise = () => {
        window.Rise = {
          full_product: { available: true },
          is_floating_cart_theme: true,
          is_product_page: true,
          product: { id: props.product.id },
          using_add_to_cart_flow: false,
        };
      };

      setTimeout(setupRise, 500);

      const script = document.createElement('script');
      script.src = 'https://str.rise-ai.com/?shop=lifeisgood-prod.myshopify.com';
      script.defer = true;
      document.body.appendChild(script);
    }
  }, [props.product.isGiftCard]);

  const isCustomizable =
    props.product.tags.some((tag) => tag === 'is_customizable::True') &&
    !document.body.classList.contains('loop-returns-activated');
  const isKidsGraphic = props.product.tags.some((tag) => tag === 'is_kids_graphic::True');
  const isHoodieGraphic = props.product.tags.some((tag) => tag === 'is_hoodie_graphic::True');
  const isTankGraphic = props.product.tags.some((tag) => tag === 'is_tank_graphic::True');
  const isDisableCustomizer = props.product.tags.some((tag) => tag === 'is_disable_customizer::True');
  const customizerBackgroundImageTag = props.product.tags.find((tag) =>
    tag.startsWith('customizer_background_image::'),
  );
  const customizerBackgroundImage = customizerBackgroundImageTag ? customizerBackgroundImageTag.split('::')[1] : '';

  return (
    <div className="flex flex-col gap-20">
      <form
        method="post"
        action="/cart/add"
        acceptCharset="UTF-8"
        encType="multipart/form-data"
        noValidate="novalidate"
        data-product-form
      >
        <div className="mb-20 px-15 md:px-0">{price}</div>
        {isMobile && gallery}

        <div className="px-15 md:px-0">
          {props.product.groupTag && compareAtPrice <= salePrice && (
            <GroupedProducts
              groupTag={props.product.groupTag}
              productHandle={props.product.handle}
              departmentTag={props.product.tags.find((tag) => tag.startsWith('department::'))}
            />
          )}
          {variantSelectors}
          <div>
            <div className="mb-[10px] flex flex-wrap gap-6">
              <QuantitySelector
                quantity={quantity}
                handleQuantityChange={handleQuantityChange}
                disabled={
                  !currentVariant.availableForSale ||
                  (currentVariant?.inventoryManagement === 'shopify' &&
                    currentVariant?.inventoryPolicy === 'deny' &&
                    quantity >= maxAvailableInventory)
                }
                maxQuantity={maxAvailableInventory}
                productTitle={props.product.title}
                max={
                  currentVariant?.inventoryManagement === 'shopify' && currentVariant?.inventoryPolicy === 'deny'
                    ? maxAvailableInventory
                    : null
                }
              />

              <div
                className="w-[calc(100%-107px)] l:w-[calc(100%-136px)]"
                ref={addToCartRef}
              >
                <div ref={addToCartRef}>{addToCartButton}</div>
              </div>
            </div>
            {currentVariant?.inventoryManagement === 'shopify' &&
              currentVariant?.inventoryPolicy === 'deny' &&
              maxAvailableInventory > 0 &&
              quantity >= maxAvailableInventory && (
                <div className="text-sm mt-2 w-full text-error">
                  {window.TMW.Strings.product.limitedStock || 'Limited stock remaining!'}
                </div>
              )}
          </div>
          {delayedShippingMessage && (
            <div
              className="[&>div>p]:l:body-14-light [&>div>ol]:l:body-14-light [&>div>ul]:l:body-14-light [&>div>*>a]:l:body-14-light [&>div>p]:body-12-light [&>div>ol]:body-12-light [&>div>ul]:body-12-light [&>div>*>a]:body-12-light mt-20 [&>div>*>a]:underline [&>div>p>a]:underline-offset-2"
              dangerouslySetInnerHTML={{ __html: delayedShippingMessage }}
            ></div>
          )}
          {!isGiftBoxEligible && (
            <div className="body-12-light mt-20">This product makes your order ineligible for a gift box.</div>
          )}
          <div>
            {isCustomizable && !isDisableCustomizer && (
              <Fragment>
                <div className="body-10-light relative my-[10px] h-12 text-center before:absolute before:left-0 before:top-1/2 before:h-1 before:w-[42%] before:bg-grey-5 before:content-[''] after:absolute after:right-0 after:top-1/2 after:h-1 after:w-[42%] after:bg-grey-5 after:content-['']">
                  {window.TMW.Strings.product.or}
                </div>
                <a
                  href="#"
                  className="customizer-button cta-white mb-[10px] flex h-45 w-full items-center justify-center rounded border border-primary"
                  data-graphic-name={props.product?.metafields?.graphicName || props.product.title}
                  data-gan={props.product?.metafields?.gan}
                  data-is-kids-graphic={isKidsGraphic}
                  data-is-hoodie-graphic={isHoodieGraphic}
                  data-is-tank-graphic={isTankGraphic}
                  data-customizer-background-img-url={customizerBackgroundImage.trim()}
                  onClick={openCustomizerStyleModal}
                >
                  {window.TMW.Strings.product.customize}
                </a>
              </Fragment>
            )}
          </div>
        </div>
      </form>
      <div className="flex flex-col gap-20 px-15 md:px-0">
        {features}
        <Accordions
          accordions={props.product.accordions}
          filterAccordions={false}
          currentVariant={currentVariant}
        />
      </div>
      {showStickyAddToCart && (
        <div
          className="fixed bottom-0 left-0 right-0 top-auto z-[49] flex animate-slideUp items-center justify-between gap-20 border-t border-grey-5 bg-white px-15 py-15 md:bottom-auto md:top-0 md:border-b md:border-t-0 l:animate-slideDown
        l:px-40 l:py-20"
        >
          <div className="h4 l:h3 hidden text-primary md:block">{props.product.title}</div>
          <div className="flex w-full items-center gap-6 md:w-fit">
            <QuantitySelector
              quantity={quantity}
              handleQuantityChange={handleQuantityChange}
              disabled={
                !currentVariant.availableForSale ||
                (currentVariant?.inventoryManagement === 'shopify' &&
                  currentVariant?.inventoryPolicy === 'deny' &&
                  quantity >= maxAvailableInventory)
              }
              maxQuantity={maxAvailableInventory}
              productTitle={props.product.title}
            />
            {addToCartButton}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductForm;
